import { useEffect, useMemo, useState } from "react";

import AmbienteTema from "./AmbienteTema";
import Layout from "./Layout";
import LayoutUAT from "./LayoutUAT";
import { SalidaPendienteProvider } from "../vigilancia/SalidaPendiente";
import PestanaParpadea from "../vigilancia/PestanaParpadea";
import { TemaUatContexto } from "./SelectorDeTemas";
import {
  CLAVE_TEMA,
  CLAVE_VIDRIO,
  TEMA_ESTANDAR,
  temaDeLaOpcion,
  temaGuardado,
  temaOscuro,
  varianteDeLaOpcion,
  vidrioGuardado,
  vidrioValido,
} from "./temasUat";
import { useAuth } from "../../auth/AuthContext";
import { aplicarPrefs, leerPrefs } from "../../functions/preferencias";
import {
  guardarTemaHub,
  guardarVidrioHub,
  temaHubDe,
  traerPrefs,
  vidrioDe,
} from "../../services/preferencias";

// El tema y el puente de tokens tienen que estar disponibles en LOS DOS
// armazones (barra y rail), así que se importan acá, que es común a ambos.
import "../../assets/css/temas.css";
import "../../assets/css/temas-clubes.css";
import "../../assets/css/armazon-uat.css";

/* CONMUTADOR DE ARMAZÓN (sólo UAT).
 *
 * Igna (25/09): que la barra superior y el rail lateral queden como una OPCIÓN,
 * y que los 13 temas anden independientes en cualquiera de los dos.
 *
 * Este componente es la única fuente de verdad del TEMA (`data-tema` en la raíz)
 * y del ARMAZÓN elegido. Aplica el tema siempre —así el puente de tokens themea
 * también el armazón viejo (rail)— y renderiza uno u otro shell. Un panelito
 * flotante deja cambiar ambas cosas desde donde sea.
 *
 * Nada de esto va a producción: es el shell de la rama de prueba.
 */

const CLAVE_ARMAZON = "uat-armazon";

const leer = (clave, def) => {
  try {
    return localStorage.getItem(clave) || def;
  } catch {
    return def;
  }
};

const LayoutSwitcher = () => {
  // De arranque, el menú al costado: es el de siempre. La barra de arriba se
  // elige en Preferencias (antes era un panel flotante de prueba, y la barra
  // era lo primero que veía cualquiera).
  const [armazon, setArmazon] = useState(() => leer(CLAVE_ARMAZON, "rail"));
  const [tema, setTema] = useState(temaGuardado);
  const [vidrio, setVidrio] = useState(vidrioGuardado);
  const { fullUser } = useAuth();
  // Estándar es el hub sin tema (ver `temasUat.js`): ni atributo, ni modo
  // forzado, ni otra vista que el rail de siempre.
  const estandar = tema === TEMA_ESTANDAR;

  /* EL TEMA DE LA CUENTA (ver `guardarTemaHub`). Al entrar se pone el que
   * tiene guardado la persona —el que eligió en otra máquina, o el que le
   * asignaron—, y pisa al de este navegador. Mientras llega se ve el de acá:
   * es un instante, y esperar a la red para pintar sería peor. */
  useEffect(() => {
    if (!fullUser?.id) return undefined;
    let vigente = true;
    traerPrefs().then((remotas) => {
      const suyo = temaHubDe(remotas);
      if (vigente && suyo) setTema(suyo);
      const opacidad = vidrioDe(remotas);
      if (vigente && opacidad) setVidrio(opacidad);
    });
    return () => {
      vigente = false;
    };
  }, [fullUser?.id]);

  /* LA OPACIDAD DE LAS TARJETAS (ver «Todas las tarjetas son vidrio» en
   * `armazon-uat.css`). Se escribe en la raíz, que es de donde la leen las
   * tarjetas; al salir se saca, para que el ingreso quede como siempre. */
  useEffect(() => {
    const raiz = document.documentElement;
    // Con «menos transparencia» pedido en el sistema las tarjetas son macizas
    // (lo dice la hoja); escribirlo acá, en la raíz, le ganaría a esa regla.
    const macizo = window.matchMedia?.("(prefers-reduced-transparency: reduce)").matches;
    if (macizo) raiz.style.removeProperty("--pf-vidrio-alfa");
    else raiz.style.setProperty("--pf-vidrio-alfa", `${vidrio}%`);
    try {
      localStorage.setItem(CLAVE_VIDRIO, String(vidrio));
    } catch {
      /* modo privado: no rompe */
    }
    return () => raiz.style.removeProperty("--pf-vidrio-alfa");
  }, [vidrio]);
  // Mientras se arrastra el deslizador sólo se ve; al soltar se guarda.
  const verVidrio = (v) => {
    const n = vidrioValido(v);
    if (n) setVidrio(n);
  };
  const fijarVidrio = (v) => {
    const n = vidrioValido(v);
    if (!n) return;
    setVidrio(n);
    guardarVidrioHub(n);
  };

  // Cuando lo elige la persona, además de ponerse se guarda en su cuenta.
  const elegirTema = (t) => {
    setTema(t);
    guardarTemaHub(t);
  };

  // El tema manda por `data-tema` en la raíz, en cualquier armazón. Una
  // variante («Venom (cara)») pone el de su tema y, al lado, `data-variante`
  // (ver `VARIANTES_DE_TEMA`); lo que se guarda es la opción.
  useEffect(() => {
    const raiz = document.documentElement;
    const variante = varianteDeLaOpcion(tema);
    if (estandar) raiz.removeAttribute("data-tema");
    else raiz.setAttribute("data-tema", temaDeLaOpcion(tema));
    if (variante) raiz.setAttribute("data-variante", variante);
    else raiz.removeAttribute("data-variante");
    try {
      localStorage.setItem(CLAVE_TEMA, tema);
    } catch {
      /* modo privado: no rompe */
    }
    return () => {
      raiz.removeAttribute("data-tema");
      raiz.removeAttribute("data-variante");
    };
  }, [tema, estandar]);

  /* El modo claro/oscuro lo dicta el tema, no la preferencia de la persona.
   *
   * Con un tema claro y la preferencia en oscuro (o al revés) quedaban las dos
   * cosas mezcladas: las reglas viejas que preguntan por `data-theme="dark"`
   * pintaban filas oscuras adentro de Papel. `aplicarPrefs` vuelve a escribir
   * el atributo cada vez que cambia una preferencia, así que no alcanza con
   * ponerlo una vez: se lo vigila. */
  useEffect(() => {
    // En Estándar manda la preferencia de la persona, como en producción. Al
    // volver de un tema hay que reponerla: el tema la había pisado.
    if (estandar) {
      if (fullUser?.id) aplicarPrefs(leerPrefs(fullUser.id));
      return undefined;
    }
    const raiz = document.documentElement;
    const modo = temaOscuro(tema) ? "dark" : "light";
    const fijar = () => {
      if (raiz.getAttribute("data-theme") !== modo)
        raiz.setAttribute("data-theme", modo);
    };
    fijar();
    const ojo = new MutationObserver(fijar);
    ojo.observe(raiz, { attributes: true, attributeFilter: ["data-theme"] });
    return () => ojo.disconnect();
  }, [tema, estandar, fullUser?.id]);

  const elegirArmazon = (a) => {
    setArmazon(a);
    try {
      localStorage.setItem(CLAVE_ARMAZON, a);
    } catch {
      /* no rompe */
    }
  };

  // Para el selector de temas de Preferencias, que vive adentro del armazón.
  const contexto = useMemo(
    () => ({
      tema,
      setTema: elegirTema,
      armazon,
      setArmazon: elegirArmazon,
      vidrio,
      verVidrio,
      fijarVidrio,
    }),
    [tema, armazon, vidrio],
  );

  return (
    <TemaUatContexto.Provider value={contexto}>
      {/* El aviso de salidas pendientes de la barrera (ticket 417) va acá,
          común a los dos armazones: la campana la dibuja cada cabecera. */}
      <SalidaPendienteProvider>
        {armazon === "rail" || estandar ? (
          <Layout />
        ) : (
          <LayoutUAT tema={tema} onTema={elegirTema} />
        )}
      </SalidaPendienteProvider>

      {/* La pestaña parpadea si llega un mensaje y no se está mirando. */}
      <PestanaParpadea />

      <AmbienteTema tema={tema} armazon={estandar ? "rail" : armazon} />
    </TemaUatContexto.Provider>
  );
};

export default LayoutSwitcher;
